import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from '@nexdel/core/utils/icons';
import { useGlobalSearch, type GlobalSearchResult } from '@nexdel/core/hooks/use-global-search';
import { entityIcons } from '@nexdel/core/utils/entityIcons';
import { Dialog, DialogContent } from '../components/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../components/command';

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ENTITY_ROUTES: Record<string, string> = {
  supplier: '/suppliers',
  contact: '/contacts',
  mandate: '/mandates',
  requirement: '/requirements',
};

export function SearchModal({ open, onOpenChange }: SearchModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Reset on close
  useEffect(() => {
    if (!open) {
      setInput('');
      setDebouncedQ('');
    }
  }, [open]);

  // 300ms debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(input), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const { data, isFetching } = useGlobalSearch(debouncedQ);

  const handleSelect = (result: GlobalSearchResult) => {
    navigate(`${ENTITY_ROUTES[result.entityType]}/${result.id}`);
    onOpenChange(false);
  };

  const hasResults =
    data &&
    (data.suppliers.length > 0 ||
      data.contacts.length > 0 ||
      data.mandates.length > 0 ||
      data.requirements.length > 0);

  const SupplierIcon = entityIcons.supplier;
  const ContactIcon = entityIcons.contact;
  const MandateIcon = entityIcons.mandate;
  const RequirementIcon = entityIcons.requirement;

  const iconFor = (entityType: string) => {
    switch (entityType) {
      case 'supplier': return <SupplierIcon size={14} className="shrink-0 text-muted-foreground" />;
      case 'contact': return <ContactIcon size={14} className="shrink-0 text-muted-foreground" />;
      case 'mandate': return <MandateIcon size={14} className="shrink-0 text-muted-foreground" />;
      case 'requirement': return <RequirementIcon size={14} className="shrink-0 text-muted-foreground" />;
      default: return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-lg max-w-lg">
        {/* shouldFilter=false — results come from server FTS, no client re-filter */}
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-4 [&_[cmdk-input-wrapper]_svg]:w-4 [&_[cmdk-input]]:h-11 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2">
          <div className="relative">
            <CommandInput
              placeholder={t('search.placeholder')}
              value={input}
              onValueChange={setInput}
            />
            {isFetching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 size={14} className="animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          <CommandList className="max-h-[400px]">
            {debouncedQ.trim().length < 2 ? (
              <div className="py-6 text-center text-xs text-muted-foreground">
                {t('search.typeToSearch')}
              </div>
            ) : !isFetching && !hasResults ? (
              <CommandEmpty>{t('search.noResults')}</CommandEmpty>
            ) : null}

            {data && data.suppliers.length > 0 && (
              <CommandGroup heading={t('suppliers.title')}>
                {data.suppliers.map(r => (
                  <CommandItem key={r.id} value={r.id} onSelect={() => handleSelect(r)} className="gap-2 text-xs">
                    {iconFor(r.entityType)}
                    <span className="font-medium truncate">{r.title}</span>
                    {r.subtitle && <span className="text-muted-foreground truncate">{r.subtitle}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {data && data.contacts.length > 0 && (
              <>
                {data.suppliers.length > 0 && <CommandSeparator />}
                <CommandGroup heading={t('contacts.title')}>
                  {data.contacts.map(r => (
                    <CommandItem key={r.id} value={r.id} onSelect={() => handleSelect(r)} className="gap-2 text-xs">
                      {iconFor(r.entityType)}
                      <span className="font-medium truncate">{r.title}</span>
                      {r.subtitle && <span className="text-muted-foreground truncate">{r.subtitle}</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {data && data.mandates.length > 0 && (
              <>
                {(data.suppliers.length > 0 || data.contacts.length > 0) && <CommandSeparator />}
                <CommandGroup heading={t('mandates.title')}>
                  {data.mandates.map(r => (
                    <CommandItem key={r.id} value={r.id} onSelect={() => handleSelect(r)} className="gap-2 text-xs">
                      {iconFor(r.entityType)}
                      <span className="font-medium truncate">{r.title}</span>
                      {r.subtitle && <span className="text-muted-foreground font-mono truncate">{r.subtitle}</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {data && data.requirements.length > 0 && (
              <>
                {(data.suppliers.length > 0 || data.contacts.length > 0 || data.mandates.length > 0) && <CommandSeparator />}
                <CommandGroup heading={t('requirements.title')}>
                  {data.requirements.map(r => (
                    <CommandItem key={r.id} value={r.id} onSelect={() => handleSelect(r)} className="gap-2 text-xs">
                      {iconFor(r.entityType)}
                      <span className="font-medium truncate">{r.title}</span>
                      {r.subtitle && <span className="text-muted-foreground truncate">{r.subtitle}</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
